import React, {useState} from 'react';
import {useMutation, useQueryClient} from 'react-query';
import {
  Modal,
  PrimaryButton,
  customStyles,
  DropdownIndicator,
} from 'components';
import {uploadCsv} from 'assets/images/images';
import {toast} from 'react-toastify';
import {bulkScreening} from 'requests/queries/aml';
import {useSelector} from 'react-redux';
import {useAllApps} from 'hooks/useApps';
import useDragAndDrop from 'hooks/useDragAndDrop';
import Select from 'react-select';

function BatchAMLModal({show, onClose, onSuccessRefetch, companyTitles, user}) {
  const queryClient = useQueryClient();
  const [file, setFileData] = useState(null);
  const [entityType, setEntityType] = useState('individual');
  const [title, setTitle] = useState('');
  const [titleError, setTitleError] = useState('');
  const [templateLink, setTemplateLink] = useState(
    'https://files.dojah.io/1752057468554-AML_INDIVIDUAL_SCREENING_TEMPLATE.xlsx',
  );
  const {userDetails} = useSelector(state => state?.auth);
  const {appIds} = useAllApps();
  const {dragOver, setDragOver, onDragOver, onDragLeave} = useDragAndDrop();
  const maxTitleLength = 30;

  const templateLinks = {
    individual:
      'https://files.dojah.io/1752057468554-AML_INDIVIDUAL_SCREENING_TEMPLATE.xlsx',
    organization:
      'https://files.dojah.io/1752057381347-AML_ORGANISATION_SCREENING_TEMPLATE.xlsx',
  };

  const onDrop = e => {
    e.preventDefault();
    setDragOver(false);
    const selectedFile = e?.dataTransfer?.files[0];
    if (
      selectedFile &&
      (selectedFile.type === 'application/vnd.ms-excel' ||
        selectedFile.type === 'text/csv' ||
        selectedFile.type ===
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')
    ) {
      setFileData(selectedFile);
    } else {
      toast.error('Please provide a valid file type (.csv or .xlsx)');
    }
  };

  const handleTitleChange = e => {
    const value = e.target.value;
    if (value.length <= maxTitleLength) {
      setTitle(value);
      if (
        value &&
        companyTitles.some(
          titleObj => titleObj.title && titleObj.title.trim() === value.trim(),
        )
      ) {
        setTitleError(
          'This title is already in use. Please choose a different title.',
        );
      } else {
        setTitleError('');
      }
    }
  };

  const handleEntityTypeChange = selectedOption => {
    const newEntityType = selectedOption.value;
    setEntityType(newEntityType);
    setTemplateLink(templateLinks[newEntityType]);
  };

  const mutation = useMutation(
    data => {
      const formData = new FormData();
      formData.append('file', data.file);
      formData.append('title', data.title.trim());
      formData.append('schema', data.entityType);
      formData.append(
        'created_by',
        `${data.user?.first_name} ${data.user?.last_name}`,
      );
      formData.append('company_id', data.userDetails?.company?.id);
      formData.append('app_id', data.appIds);

      return bulkScreening({data: formData});
    },
    {
      onSuccess: () => {
        toast.success('File uploaded successfully');
        onClose();
        setFileData(null);
        setTitle('');
        setTitleError('');
        queryClient.invalidateQueries(['batch-aml-screenings']);
        if (onSuccessRefetch) {
          onSuccessRefetch();
        }
      },
      onError: error => {
        toast.error(
          `Upload failed: ${error.response?.data?.error || error.message}`,
        );
      },
    },
  );

  const entityTypeOptions = [
    {value: 'individual', label: 'Individual'},
    {value: 'organization', label: 'Organization'},
  ];

  const handleFileChange = e => {
    const selectedFile = e.target.files[0];
    if (
      selectedFile &&
      (selectedFile.type === 'application/vnd.ms-excel' ||
        selectedFile.type === 'text/csv' ||
        selectedFile.type ===
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')
    ) {
      setFileData(selectedFile);
    } else {
      toast.error('Please provide a valid file type (.csv or .xlsx)');
      setFileData(null);
    }
  };

  const handleSubmit = e => {
    e.preventDefault();
    if (titleError) {
      return;
    }
    if (
      file &&
      title &&
      userDetails?.company?.id &&
      user?.first_name &&
      user?.last_name &&
      appIds
    ) {
      mutation.mutate({
        file,
        title,
        entityType,
        userDetails,
        user,
        appIds,
      });
    } else {
      setTitleError(!title ? 'Please provide a title.' : '');
      toast.error('Missing required details, file, or title');
    }
  };

  return (
    <Modal
      show={show}
      onClose={onClose}
      modalTitle="Batch Search"
      extraModalClasses="max-w-[480px] overflow-y-auto h-fit max-h-[95%] sm:max-h-full rounded-b-none sm:rounded-b-lg absolute bottom-0 sm:relative lookup-modal"
      modalPosition="justify-center sm:justify-end"
    >
      <div className="mt-6 px-6 rounded">
        <div className="mb-4 text-left">
          <label className="text-tiny text-[#4E4E4E] mb-[6px] font-normal">
            Entity type
          </label>
          <Select
            defaultValue={entityTypeOptions.find(
              option => option.value === entityType,
            )}
            value={entityTypeOptions.find(
              option => option.value === entityType,
            )}
            onChange={handleEntityTypeChange}
            options={entityTypeOptions}
            placeholder="Select entity type"
            components={{DropdownIndicator}}
            classNamePrefix="react-select"
            styles={customStyles}
            menuPlacement="auto"
            isSearchable={false}
            isDisabled={mutation.isLoading}
            id="entity-type"
          />
        </div>
        <div className="mb-4">
          <label htmlFor="title" className="relative mt-6 mb-2">
            Title
          </label>
          <input
            id="title"
            type="text"
            value={title}
            onChange={handleTitleChange}
            maxLength={maxTitleLength}
            className="!h-[36px]"
            placeholder="Enter batch title"
          />
          <div className="flex justify-between items-center mt-1">
            <small className="text-red-800 text-xs">{titleError}</small>
            <p className="text-xs text-grey-700 text-right">
              {title.length}/{maxTitleLength} characters
            </p>
          </div>
        </div>
        <div
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          style={{
            border: `${dragOver ? '3px dashed #33333322' : ''}`,
          }}
          className="py-[22px] flex flex-col items-center gap-[2px] text-center border border-brandBlue border-dashed rounded"
        >
          <div className="flex-col items-center hidden sm:flex">
            {!file && <img src={uploadCsv} alt="" width={48} height={48} />}
            <p className="text-body">
              {dragOver ? (
                'Please drop file here'
              ) : (
                <span style={{color: file ? '#00000032' : ''}}>
                  Drag and drop CSV file here
                </span>
              )}
            </p>
          </div>
          {file ? (
            <div className="text-body text-sm flex my-2 gap-[3px] items-baseline">
              <input
                type="file"
                id="file"
                onChange={handleFileChange}
                accept=".xlsx, .csv"
              />
              <label htmlFor="file">Change file</label>
            </div>
          ) : (
            <div className="text-body text-sm flex gap-[3px] items-baseline">
              <span className="hidden sm:block">or</span>
              <input
                type="file"
                id="file"
                onChange={handleFileChange}
                accept=".xlsx, .csv"
              />
              <label htmlFor="file">click here to select</label>
              from your device
            </div>
          )}
          <p className="text-body text-sm flex gap-[3px] items-baseline">
            {file ? (
              <>
                File: <span className="font-bold text-black">{file.name}</span>
              </>
            ) : (
              ''
            )}
          </p>
        </div>

        <div className="mt-2 text-xs text-right">
          <a
            href={templateLink}
            rel="noreferrer"
            download
            className="font-medium text-body sm:text-brandBlue"
          >
            Click here{' '}
            <span className="font-medium text-body">
              to download template for batch upload
            </span>
          </a>
        </div>
      </div>

      <div className="w-full mt-6 px-6 py-[14px] border-[0.5px] border-transparent border-t-[#EFEFEF]">
        <PrimaryButton
          buttonText="Perform Search"
          className="w-full !h-10"
          onClick={handleSubmit}
          loading={mutation.isLoading}
          disabled={
            !file ||
            !title ||
            titleError ||
            !userDetails?.company?.id ||
            !user?.first_name ||
            !user?.last_name ||
            !appIds ||
            mutation.isLoading
          }
        />
      </div>
    </Modal>
  );
}

export default BatchAMLModal;
